import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AlertCircle, Check, Pencil, Trash2 } from 'lucide-react';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import sentinelNetwork from '@/lib/api/sentinelNetwork';
import { validatePolarisConnection } from '@/app/onboarding-secops/api';
import { parsePolarisApplicationId } from '@/app/onboarding-secops/validation';
import PolarisAppIdInstructions from '@/components/PolarisAppIdInstructions';

interface PolarisIntegrationProps {
  appId: string | undefined;
  projectTokens: any[];
  onTokenChange: () => Promise<void>;
  onSnackbar: (message: string, severity: 'success' | 'error') => void;
}

/**
 * Polaris connection card — unlike SonarQube/BlackDuck/Trivy, no credential is ever
 * entered here. Sentinel authenticates to Polaris with one centrally managed
 * service-account token (Secrets Manager only). Only the project's Polaris
 * application UUID is collected and validated before saving.
 * See docs/POLARIS_ONBOARDING.md.
 */
export default function PolarisIntegration({
  appId,
  projectTokens,
  onTokenChange,
  onSnackbar,
}: PolarisIntegrationProps) {
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [isReplacing, setIsReplacing] = useState(false);
  const [validation, setValidation] = useState<{ loading: boolean; saving: boolean; result: { ok: boolean; message: string } | null }>({ loading: false, saving: false, result: null });

  const polarisToken = projectTokens.find((t: any) => t.platform === 'Polaris');

  const resetForm = () => {
    setApplicationId('');
    setIsReplacing(false);
    setValidation({ loading: false, saving: false, result: null });
  };

  const saveConfig = async (id: string) => {
    setValidation((v) => ({ ...v, saving: true }));
    try {
      await sentinelNetwork.post(`/config/project/${appId}/polaris`, { applicationId: id });
      setValidation({ loading: false, saving: false, result: { ok: true, message: t('projectSettings.polaris.saved') } });
      onSnackbar(t('projectSettings.polaris.saveSuccess'), 'success');
      await onTokenChange();
      resetForm();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      setValidation({ loading: false, saving: false, result: { ok: false, message: `${t('projectSettings.polaris.saveFailed')}: ${msg}` } });
      onSnackbar(t('projectSettings.polaris.saveError'), 'error');
    }
  };

  const handleVerify = async () => {
    const id = parsePolarisApplicationId(applicationId);
    if (!id) {
      setValidation({ loading: false, saving: false, result: { ok: false, message: t('projectSettings.polaris.invalidInput') } });
      return;
    }
    // Normalize a pasted URL to the bare UUID so the field reflects what was detected.
    if (id !== applicationId.trim()) setApplicationId(id);

    setValidation({ loading: true, saving: false, result: null });
    try {
      const result = await validatePolarisConnection(id);
      if (!result.ok) {
        setValidation({ loading: false, saving: false, result: { ok: false, message: result.message || t('projectSettings.polaris.validationFailed') } });
        return;
      }
      await saveConfig(id);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      setValidation({ loading: false, saving: false, result: { ok: false, message: `${t('projectSettings.polaris.requestFailed')}: ${msg}` } });
    }
  };

  const handleRemove = async () => {
    try {
      await sentinelNetwork.delete(`/config/project/${appId}/polaris`);
      onSnackbar(t('projectSettings.polaris.removeSuccess'), 'success');
      await onTokenChange();
    } catch {
      onSnackbar(t('projectSettings.polaris.removeError'), 'error');
    }
  };

  const canSubmit = !validation.loading && !validation.saving && !!applicationId.trim();

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <DeviceHubIcon fontSize="small" color="primary" />
        <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary' }}>
          {t('projectSettings.polaris.title')}
        </Typography>
        {polarisToken ? (
          <Chip icon={<Check size={12} />} label={t('projectSettings.polaris.connected')} size="small" color="success" sx={{ fontSize: 11, height: 22 }} />
        ) : (
          <Chip label={t('projectSettings.polaris.notConfigured')} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
        )}
      </Box>

      {polarisToken && !isReplacing ? (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, flex: 1 }} noWrap>
              {polarisToken.applicationId}
            </Typography>
            <Button size="small" color="primary" variant="text" startIcon={<Pencil size={14} />} onClick={() => { setIsReplacing(true); setApplicationId(''); }}>
              {t('projectSettings.polaris.replace')}
            </Button>
            <Button size="small" color="error" variant="text" startIcon={<Trash2 size={14} />} onClick={handleRemove}>
              {t('projectSettings.polaris.remove')}
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <PolarisAppIdInstructions />
          <TextField
            label={t('projectSettings.polaris.uuidLabel')}
            placeholder={t('projectSettings.polaris.uuidPlaceholder')}
            value={applicationId}
            onChange={(e) => { setApplicationId(e.target.value); setValidation({ loading: false, saving: false, result: null }); }}
            size="small"
            fullWidth
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleVerify}
              disabled={!canSubmit}
              startIcon={validation.loading ? <CircularProgress size={14} /> : <WifiTetheringIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none' }}
            >
              {validation.loading ? t('projectSettings.polaris.validating') : t('projectSettings.polaris.validateSave')}
            </Button>
            {isReplacing && (
              <Button size="small" onClick={resetForm} sx={{ textTransform: 'none' }}>
                {t('projectSettings.polaris.cancel')}
              </Button>
            )}

            {!validation.loading && !validation.saving && (
              validation.result?.ok
                ? <Chip icon={<Check size={14} />} label={validation.result.message} size="small" color="success" variant="outlined" />
                : validation.result
                  ? <Chip icon={<AlertCircle size={14} />} label={validation.result.message} size="small" color="error" variant="outlined" sx={{ maxWidth: 360, '& .MuiChip-label': { whiteSpace: 'normal' } }} />
                  : null
            )}
            {validation.saving && <Chip icon={<CircularProgress size={12} />} label={t('projectSettings.polaris.saving')} size="small" variant="outlined" />}
          </Box>
        </Stack>
      )}
    </Box>
  );
}
